// Slip Health Score panel (S1).
//
// v2.1.1 — factor id 'wp' was renamed to 'div' (Diversification);
// the label/desc come from the data layer, this component just
// renders them.
import type { ReactElement } from "react";
import type { RepairMode, SlipMeta } from "../_stubData.ts";
import { bandCopy, bandFromScore, bandLabel } from "./SrIcon.tsx";

interface Props {
  meta: SlipMeta;
  mode: RepairMode;
}

function factorClass(v: number): "high" | "good" | "med" | "low" {
  if (v >= 75) return "high";
  if (v >= 55) return "good";
  if (v >= 35) return "med";
  return "low";
}

export function SlipHealth({ meta, mode }: Props): ReactElement {
  const h = meta.health;
  const band = bandFromScore(h.score);
  const afterScore = mode === "sharp" ? h.scoreAfterSharp : h.scoreAfterSafer;
  const afterBand = bandFromScore(afterScore);
  const delta = afterScore - h.score;
  return (
    <div className={`sr-health band-${band}`}>
      <div className="sr-health-grid">
        <div className="sr-health-score">
          <div className="sr-health-kicker">
            <span>Slip Health Score</span>
            <span className="info-pip">· Original slip · pre-repair</span>
          </div>
          <div className="sr-health-num-row">
            <span className="sr-health-num">{h.score}</span>
            <span className="sr-health-out-of">/100</span>
            <span className="sr-health-band">
              <span className="pip" />
              {bandLabel(band)}
            </span>
          </div>
          <div className="sr-health-msg">{bandCopy(band)}</div>
          <div className="sr-health-after">
            <span>After repair</span>
            <span className="arrow">→</span>
            <span className="after-num">{afterScore}</span>
            <span className="after-band">{bandLabel(afterBand)}</span>
            <span className="delta">+{delta} points</span>
          </div>
        </div>

        <div className="sr-health-factors">
          <h4>Score breakdown · weighted composite</h4>
          {h.factors.filter((f) => f.id !== "void").map((f) => (
            <div className={`sr-factor ${factorClass(f.value)}`} key={f.id}>
              <div className="nm">
                {f.label}
                <span className="weight">· {f.weight}%</span>
                <span className="desc">{f.desc}</span>
              </div>
              <div className="bar">
                <div className="fill" style={{ width: `${f.value}%` }} />
              </div>
              <div className="pct">{f.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
